import React, { useMemo, useState, useEffect } from "react";
import { IconButton, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import * as XLSX from 'xlsx';

import {
  View,
  ScrollView,
  TextInput,
  Text,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useNavigation } from "@react-navigation/native";
import { useSelector } from "react-redux";
import { translateText } from "../../translations/translationService";
import { SCREEN_IDS } from "../FullComponent.js";
import { esFornitura } from "../../constants/Constants.js";
import { getPrecioProcesosTotal } from "../../services/ConnectDataBase";
import { getPrecioTotalPorRango } from "../../services/Functions.js";
import MiniResumenTabla from "../Common/ComponentMiniResumenTabla.js";
import { BACKGROUND_COLORS, BORDER_COLORS, TEXT_COLORS, MUI_COLORS, CONSULTA_PIEZAS_COLORS, CONSULTA_PIEZAS_TABLA_COLORS } from "../../style/colors.js";

// Constantes para la configuración del componente
const COMPONENT_CONFIG = {
  TABLE: {
    CELL_PADDING: "8px 12px",
    CELL_WIDTH: "25%",
    BORDER_STYLE: `1px solid #e5e7eb`,
  },
  MODAL: {
    MAX_HEIGHT: "70%",
    MIN_WIDTH: "60%",
    BORDER_RADIUS: 15,
    PADDING: 20,
  },
  EXCEL: {
    COLUMN_WIDTHS: {
      CATEGORY: 25,
      VALUE: 15,
      TYPE: 15,
      NUMBER: 8,
      MO: 15,
      CLIENT: 30,
      REFERENCE: 20,
      QUANTITY: 18,
      PRICE: 18,
      DESCRIPTION: 50,
    }
  }
};

// Configuración de estilos comunes
const COMMON_STYLES = {
  cellBase: {
    align: 'center',
    verticalAlign: "middle",
    padding: "6px 10px",
    width: COMPONENT_CONFIG.TABLE.CELL_WIDTH,
    borderBottom: "1px solid #9bb1bb",
    fontSize: "13px",
    fontWeight: 400,
  },
  headerCell: {
    color: "#174045",
    backgroundColor: "#ffffff",
    fontWeight: 600,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "6px 10px",
  },
  secondaryCell: {
    color: "#174045",
    backgroundColor: "#ffffff",
    fontWeight: 600,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    padding: "6px 10px",
  },
  totalRow: {
    backgroundColor: "#ffffff",
    borderTop: "2px solid #174045",
  },
  totalCell: {
    color: "#374151",
    fontWeight: 600,
    fontSize: "14px",
  }
};

const ConsultaPiezasTabla = ({
  data = [],
  dataFull = [],
  isLoading = false,
  onNavigateToMO,
  translateText,
  lang = "es",
}) => {

  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [modalVisible, setModalVisible] = useState(true);
  const [modalData, setModalData] = useState({ titulo: "", mos: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [datosTotal, setDatosTotal] = useState(0);

  const navigation = useNavigation();
  // Determinar si los datos completos están cargados
  const datosCompletosListos = dataFull && dataFull.length > 0;

  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", () => {
      if (showModal) {
        setModalVisible(true);
      }
    });

    return unsubscribe;
  }, [navigation, showModal]);

  useEffect(() => {
    if (showModal) {
      setModalVisible(true);
    }
  }, [showModal]);

  useEffect(() => {
    const fetchPrecioTotal = async () => {
      if (fechaInicio && fechaFin) {
        try {
          const response = await getPrecioProcesosTotal(
            "PrecioProcesosTotal",
            SCREEN_IDS.GENERAL,
            fechaInicio,
            fechaFin
          );

          const precioGeneral = getPrecioTotalPorRango(
            response,
            clienteSeleccionado
          );

          setDatosTotal(precioGeneral);
        } catch (error) {
          console.error("Error cargando referencias:", error);
        }
      }
    };

    fetchPrecioTotal();
  }, [fechaInicio, fechaFin, clienteSeleccionado]);

  // Funciones auxiliares
  const calcularPrecioTotal = (item) => {
    if (!item.procesos || !Array.isArray(item.procesos)) {
      return 0;
    }

    const cantidadPendiente = parseFloat(item.cantidadPendiente || 0);

    // Calcular el costo total de todos los procesos de esta MO
    const costoProcesos = item.procesos.reduce((total, proceso) => {
      const precio = parseFloat(proceso.precio || 0);
      const tiempo = parseFloat(proceso.tiempo || 0);
      const costoProceso = precio * tiempo; // Costo de este proceso
      return total + costoProceso;
    }, 0);

    // Multiplicar por la cantidad de piezas pendientes
    return costoProcesos * cantidadPendiente;
  };

  const getCellStyle = (isClickable, isTotal = false) => ({
    ...COMMON_STYLES.cellBase,
    cursor: isClickable ? "pointer" : "default",
    backgroundColor: "#ffffff",
    color: isTotal ? "#374151" : "#4b5563",
    fontWeight: isTotal ? 600 : 400,
    fontSize: isTotal ? "14px" : "13px",
    padding: isTotal ? "8px 10px" : "6px 10px",
    transition: "background-color 0.15s ease",
  });

  const getHeaderCellStyle = (isPrimary = false) => ({
    ...COMMON_STYLES.cellBase,
    ...(isPrimary ? COMMON_STYLES.headerCell : COMMON_STYLES.secondaryCell),
    backgroundColor: "#F2F2F2",
  });

  const clientesUnicos = useMemo(() => {
    const set = new Set(data.map((d) => d.cliente?.trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const datosFiltrados = useMemo(() => {
    return data.filter((item) => {
      const clienteOk =
        !clienteSeleccionado || item.cliente?.trim() === clienteSeleccionado;

      const fechaEntrega = item.fechaEntrega?.split(" ")[0];

      let fechaOk = true;
      if (fechaInicio && fechaEntrega) {
        fechaOk = fechaOk && fechaEntrega >= fechaInicio;
      }
      if (fechaFin && fechaEntrega) {
        fechaOk = fechaOk && fechaEntrega <= fechaFin;
      }

      return clienteOk && fechaOk;
    });
  }, [data, clienteSeleccionado, fechaInicio, fechaFin]);

  const resumen = useMemo(() => {
    let cantidadTotal = 0;
    let cantidadPendiente = 0;
    let cantidadTerminada = 0;
    let piezasCordoba = 0;
    let piezasMixtas = 0;
    let precioTotalPiezas = 0;

    let numeroFornituras = 0;
    let precioTotalFornituras = 0;

    let precioTotal = 0;

    let mosTotal = [];
    let mosPendientes = [];
    let mosTerminadas = [];
    let mosCordoba = [];
    let mosMixtas = [];
    let mosPrecioPiezas = [];

    let mosFornituras = [];
    let mosPrecioFornituras = [];

    let mosPrecio = [];

    datosFiltrados.forEach((item) => {

      const cantidad = parseFloat(item.cantidad || 0);
      const pendiente = parseFloat(item.cantidadPendiente || 0);
      const terminada = cantidad - pendiente;
      const tipo = item.tipo_orden?.toLowerCase() || "";
      const precio = calcularPrecioTotal(item);

      const referencia = item.referencia || "";
      const esFornituraItem = esFornitura(referencia);

      precioTotal += precio;
      if (precio > 0) mosPrecio.push(item);
          
      if (esFornituraItem) {
        numeroFornituras += pendiente;
        precioTotalFornituras += precio;
        if (pendiente > 0) mosFornituras.push(item);
        if (precio > 0) mosPrecioFornituras.push(item);
      } else {
        cantidadTotal += cantidad;
        cantidadPendiente += pendiente;
        cantidadTerminada += terminada;
        precioTotalPiezas += precio;

        if (cantidad > 0) mosTotal.push(item);
        if (pendiente > 0) mosPendientes.push(item);
        if (terminada > 0) mosTerminadas.push(item);
        if (precio > 0) mosPrecioPiezas.push(item);

        if (tipo.includes("mixta")) {
          piezasMixtas += pendiente;
          if (pendiente > 0) mosMixtas.push(item);
        } else {
          piezasCordoba += pendiente;
          if (pendiente > 0) mosCordoba.push(item);
        }
      }
    });

    return {
      cantidadTotal,
      cantidadPendiente,
      cantidadTerminada,
      piezasCordoba,
      piezasMixtas,
      precioTotalPiezas,
      numeroFornituras,
      precioTotalFornituras,
      precioTotal,
      mosTotal,
      mosPendientes,
      mosTerminadas,
      mosCordoba,
      mosMixtas,
      mosPrecioPiezas,
      mosFornituras,
      mosPrecioFornituras,
      mosPrecio,
    };
  }, [datosFiltrados]);

  const handleCellClick = (titulo, mos) => {
    if (mos.length > 0) {
      setModalData({ titulo, mos });
      setShowModal(true);
      setSearchTerm("");
      setScrollPosition(0);
    }
  };

  const handleMOClick = (mo) => {
    setModalVisible(false);
    setSearchTerm("");
    if (onNavigateToMO) {
      onNavigateToMO(mo);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalVisible(true);
    setSearchTerm("");
    setScrollPosition(0);
  };

  const filteredMos = modalData.mos.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      item.mo?.toLowerCase().includes(term) ||
      item.cliente?.toLowerCase().includes(term) ||
      item.referencia?.toLowerCase().includes(term) ||
      String(item.cantidad)?.toLowerCase().includes(term)
    );
  });

  const calcularPrecioIndividual = (item) => {
    return calcularPrecioTotal(item);
  };

  // Componente reutilizable para celdas clickeables
  const ClickableCell = ({ valor, titulo, mos, children, isTotal = false }) => {
    const isClickable = mos && mos.length > 0;
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
      <TableCell
        align='center'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...getCellStyle(isClickable, isTotal),
          backgroundColor: isHovered && isClickable ? "#f3f4f6" : "#ffffff",
        }}
        onClick={() => isClickable && handleCellClick(titulo, mos)}>
        {children || (
          <span style={{
            color: isTotal ? "#374151" : "#4b5563",
            fontWeight: isTotal ? 600 : 400,
          }}>
            {valor}
          </span>
        )}
      </TableCell>
    );
  };

  // Componente para celdas de encabezado
  const HeaderCell = ({ children, isPrimary = false, style = {} }) => (
    <TableCell
      align='center'
      style={{
        ...getHeaderCellStyle(isPrimary),
        ...style,
      }}>
      {children}
    </TableCell>
  );

  // Función para generar Excel del resumen general
  const generateExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      
      const excelData = [
        { 'Categoría': 'Nº Piezas Total', 'Valor': Math.round(resumen.cantidadTotal || 0), 'Tipo': 'Cantidad' },
        { 'Categoría': 'Nº Piezas Córdoba', 'Valor': Math.round(resumen.piezasCordoba || 0), 'Tipo': 'Cantidad' },
        { 'Categoría': 'Nº Piezas Pendiente', 'Valor': Math.round(resumen.cantidadPendiente || 0), 'Tipo': 'Cantidad' },
        { 'Categoría': 'Nº Piezas Mixtas', 'Valor': Math.round(resumen.piezasMixtas || 0), 'Tipo': 'Cantidad' },
        { 'Categoría': 'Nº Piezas Terminada', 'Valor': Math.round(resumen.cantidadTerminada || 0), 'Tipo': 'Cantidad' },
        { 'Categoría': 'Precio Total Piezas', 'Valor': `${(resumen.precioTotalPiezas || 0).toFixed(2)} €`, 'Tipo': 'Precio' },
        { 'Categoría': 'Nº Fornituras Pendientes', 'Valor': Math.round(resumen.numeroFornituras || 0), 'Tipo': 'Cantidad' },
        { 'Categoría': 'Precio Total Fornituras', 'Valor': `${(resumen.precioTotalFornituras || 0).toFixed(2)} €`, 'Tipo': 'Precio' },
        { 'Categoría': 'PRECIO TOTAL', 'Valor': `${datosTotal || 0} €`, 'Tipo': 'Precio Total' },
        { 'Categoría': 'PRECIO PENDIENTE', 'Valor': `${(resumen.precioTotal || 0).toFixed(2)} €`, 'Tipo': 'Precio Pendiente' }
      ];

      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.CATEGORY },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.VALUE },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.TYPE },
      ];

      const sheetName = 'Resumen_General';
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${sheetName}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
    } catch (error) {
      console.error('❌ Error generando Excel:', error);
    }
  };

  // Función para generar Excel del modal
  const generateModalExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      
      const excelData = filteredMos.map((mo, index) => ({
        'Nº': index + 1,
        'MO': mo.mo || translateText('Sin datos', lang),
        'Cliente': mo.cliente || '',
        'Referencia': mo.referencia || '',
        'Cantidad Pendiente': parseFloat(mo.cantidadPendiente || 0).toFixed(0),
        'Precio Individual': `${calcularPrecioIndividual(mo).toFixed(2)} €`,
        'Descripción': `${mo.cliente} - ${mo.referencia} - ${parseFloat(mo.cantidadPendiente || 0).toFixed(0)}`
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.NUMBER },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.MO },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.CLIENT },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.REFERENCE },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.QUANTITY },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.PRICE },
        { wch: COMPONENT_CONFIG.EXCEL.COLUMN_WIDTHS.DESCRIPTION },
      ];

      const sheetName = modalData.titulo.replace(/\s+/g, '_');
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${sheetName}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
    } catch (error) {
      console.error('❌ Error generando Excel del modal:', error);
    }
  };

  return (
    <View
      style={{
        marginTop: 25,
        borderRadius: 8,
      }}>
      {/* Filtros */}
      <Paper
        style={{
          marginBottom: 5,
          backgroundColor: BACKGROUND_COLORS.CARD,
          padding: 10,
          boxShadow: "0 -2px 6px rgba(0, 0, 0, 0.1)",
          borderTop: `1px solid ${BORDER_COLORS.LIGHT}`,
          borderBottom: `1px solid ${BORDER_COLORS.LIGHT}`,
        }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              size='small'
              value={clienteSeleccionado || null}
              onChange={(event, newValue) => {
                setClienteSeleccionado(newValue || "");
              }}
              options={["", ...clientesUnicos]}
              getOptionLabel={(option) => (option === "" ? translateText("Todos", lang) : option)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "15px",
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#174045",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                  color: "#6b7280",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#174045",
                },
                "& .MuiInputBase-input": {
                  padding: "6px 10px",
                  fontSize: "15px",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translateText("Selector clientes", lang)}
                  size='small'
                  sx={{
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-root": {
                      fontSize: "15px",
                      "& fieldset": {
                        borderColor: "#e5e7eb",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#174045",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      color: "#6b7280",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#174045",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 10px",
                      fontSize: "15px",
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option || "todos"}>
                  {option === "" ? <em>{translateText("Todos", lang)}</em> : option}
                </li>
              )}
              clearOnEscape
              selectOnFocus
              handleHomeEndKeys
              ListboxProps={{
                style: { maxHeight: 200 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              type='date'
              size='small'
              sx={{
                backgroundColor: "#ffffff",
                "& .MuiOutlinedInput-root": {
                  fontSize: "15px",
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#174045",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                  color: "#6b7280",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#174045",
                },
                "& .MuiInputBase-input": {
                  padding: "6px 10px",
                  fontSize: "15px",
                },
              }}
              fullWidth
              label={translateText("Fecha inicio", lang)}
              InputLabelProps={{ shrink: true }}
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputProps={{
                endAdornment: fechaInicio && (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      onClick={() => setFechaInicio("")}
                      edge='end'
                      sx={{ 
                        color: "#9ca3af",
                        padding: "4px",
                        "&:hover": {
                          color: "#174045",
                        },
                      }}>
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              type='date'
              size='small'
              sx={{
                backgroundColor: "#ffffff",
                "& .MuiOutlinedInput-root": {
                  fontSize: "15px",
                  "& fieldset": {
                    borderColor: "#e5e7eb",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d1d5db",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#174045",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                  color: "#6b7280",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#174045",
                },
                "& .MuiInputBase-input": {
                  padding: "6px 10px",
                  fontSize: "15px",
                },
              }}
              fullWidth
              label={translateText("Fecha fin", lang)}
              InputLabelProps={{ shrink: true }}
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputProps={{
                endAdornment: fechaFin && (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      onClick={() => setFechaFin("")}
                      edge='end'
                      sx={{ 
                        color: "#9ca3af",
                        padding: "4px",
                        "&:hover": {
                          color: "#174045",
                        },
                      }}>
                      <ClearIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={generateExcel}
                style={{
                  backgroundColor: CONSULTA_PIEZAS_TABLA_COLORS.BACKGROUND_DARK,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                📊 {translateText("Exportar a Excel", lang)}
              </button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla principal (datos pendientes) */}
      <TableContainer
        component={Paper}
        style={{
          borderRadius: "4px",
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
        }}>
        <Table size='small' sx={{ borderCollapse: "collapse" }}>
          <TableBody>
            <TableRow>
              <HeaderCell isPrimary={true}>{translateText("Nº Piezas (MOs Activas)", lang)}</HeaderCell>
              <ClickableCell
                valor={Math.round(resumen.cantidadTotal)}
                titulo={translateText("Piezas (MOs Activas)", lang)}
                mos={resumen.mosTotal}
              />
              <HeaderCell>{translateText("Nº Piezas Córdoba", lang)}</HeaderCell>
              <ClickableCell
                valor={Math.round(resumen.piezasCordoba)}
                titulo={translateText("Piezas Córdoba", lang)}
                mos={resumen.mosCordoba}
              />
            </TableRow>
            <TableRow>
              <HeaderCell>{translateText("Nº Piezas Pendiente", lang)}</HeaderCell>
              <ClickableCell
                valor={Math.round(resumen.cantidadPendiente)}
                titulo={translateText("Piezas Pendientes", lang)}
                mos={resumen.mosPendientes}
              />
              <HeaderCell>{translateText("Nº Piezas Mixtas", lang)}</HeaderCell>
              <ClickableCell
                valor={Math.round(resumen.piezasMixtas)}
                titulo={translateText("Piezas Mixtas", lang)}
                mos={resumen.mosMixtas}
              />
            </TableRow>
            <TableRow>
              <HeaderCell>{translateText("Nº Piezas Terminada", lang)}</HeaderCell>
              <ClickableCell
                valor={Math.round(resumen.cantidadTerminada)}
                titulo={translateText("Piezas Terminadas", lang)}
                mos={resumen.mosTerminadas}
              />
              <HeaderCell>{translateText("Precio Total Piezas", lang)}</HeaderCell>
              <ClickableCell
                titulo={translateText("MOs con Precio Piezas", lang)}
                mos={resumen.mosPrecioPiezas}>
                <span style={{ 
                  color: "#174045", 
                  fontWeight: 500,
                  fontSize: "13px",
                }}>
                  {resumen.precioTotalPiezas.toFixed(2)} €
                </span>
              </ClickableCell>
            </TableRow>

            <TableRow>
              <HeaderCell isPrimary={true}>{translateText("Nº Fornituras Pendientes", lang)}</HeaderCell>
              <ClickableCell
                valor={Math.round(resumen.numeroFornituras)}
                titulo={translateText("Fornituras", lang)}
                mos={resumen.mosFornituras}
              />
              <HeaderCell isPrimary={true}>{translateText("Precio Total Fornituras", lang)}</HeaderCell>
              <ClickableCell
                titulo={translateText("MOs con Precio Fornituras", lang)}
                mos={resumen.mosPrecioFornituras}>
                <span style={{ 
                  color: "#174045", 
                  fontWeight: 500,
                  fontSize: "13px",
                }}>
                  {resumen.precioTotalFornituras.toFixed(2)} €
                </span>
              </ClickableCell>
            </TableRow>

            <TableRow style={COMMON_STYLES.totalRow}>
              <HeaderCell isPrimary={true}>{translateText("PRECIO TOTAL", lang)}</HeaderCell>
              <ClickableCell titulo={translateText("Todas las MOs con Precio", lang)} mos={""} isTotal={true}>
                <span style={{ 
                  color: "#374151", 
                  fontSize: "14px",
                  fontWeight: 600,
                }}>
                  {datosTotal} €
                </span>
              </ClickableCell>
              <HeaderCell isPrimary={true}>{translateText("PRECIO PENDIENTE", lang)}</HeaderCell>
              <ClickableCell
                titulo='Todas las MOs con Precio'
                mos={resumen.mosPrecio}
                isTotal={true}>
                <span style={{ 
                  color: "#374151", 
                  fontSize: "14px",
                  fontWeight: 600,
                }}>
                  {resumen.precioTotal.toFixed(2)} €
                </span>
              </ClickableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* MiniResumenTabla con skeletons cuando está cargando */}
      <MiniResumenTabla
        data={dataFull}
        clienteSeleccionado={clienteSeleccionado}
        fechaInicio={fechaInicio}
        fechaFin={fechaFin}
        translateText={translateText}
        lang={lang}
        onNavigateToMO={onNavigateToMO}
        isLoading={!datosCompletosListos}
      />

      {/* Modal existente */}
      <Modal
        animationType='fade'
        transparent={true}
        visible={showModal && modalVisible}
        onRequestClose={handleCloseModal}>
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          }}>
          <View
            style={{
              maxHeight: "70%",
              minWidth: "60%",
              backgroundColor: CONSULTA_PIEZAS_COLORS.BACKGROUND_WHITE,
              borderRadius: 15,
              padding: 20,
              shadowColor: CONSULTA_PIEZAS_COLORS.SHADOW_DARK,
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.4,
              shadowRadius: 5,
              elevation: 10,
            }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  color: CONSULTA_PIEZAS_TABLA_COLORS.TEXT_PRIMARY,
                  flex: 1,
                  textAlign: "center",
                  marginLeft: '90px'
                }}>
                {modalData.titulo}
              </Text>
              
              {/* Botón de Excel para el modal */}
              <button
                onClick={generateModalExcel}
                style={{
                  backgroundColor: CONSULTA_PIEZAS_TABLA_COLORS.BACKGROUND_DARK,
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginLeft: 10,
                }}>
                📊 Excel
              </button>
            </View>

            <TextInput
              style={{
                marginBottom: 15,
                padding: 10,
                borderWidth: 1,
                borderColor: CONSULTA_PIEZAS_COLORS.BORDER_INPUT,
                borderRadius: 8,
                backgroundColor: CONSULTA_PIEZAS_COLORS.BACKGROUND_CARD,
                fontSize: 16,
                color: CONSULTA_PIEZAS_COLORS.TEXT_INPUT,
              }}
              placeholder={translateText("Buscar por MO, cliente, referencia, etc.", lang)}
              placeholderTextColor={CONSULTA_PIEZAS_COLORS.TEXT_PLACEHOLDER}
              value={searchTerm}
              onChangeText={setSearchTerm}
            />

            <ScrollView
              ref={(ref) => {
                if (ref && modalVisible && scrollPosition > 0) {
                  setTimeout(() => {
                    ref.scrollTo({ y: scrollPosition, animated: false });
                  }, 100);
                }
              }}
              onScroll={(event) => {
                setScrollPosition(event.nativeEvent.contentOffset.y);
              }}
              scrollEventThrottle={16}
              style={{
                flex: 1,
                backgroundColor: CONSULTA_PIEZAS_COLORS.BACKGROUND_WHITE,
                borderRadius: 8,
                padding: 10,
              }}>
              <View style={{ flexDirection: "column" }}>
                {modalData.mos.length > 0 ? (
                  filteredMos.map((mo, index) => (
                    <TouchableOpacity
                      key={index}
                      style={{
                        position: "relative",
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 8,
                        paddingHorizontal: 12,
                        backgroundColor: CONSULTA_PIEZAS_COLORS.BACKGROUND_SEARCH,
                        borderRadius: 8,
                        marginBottom: 10,
                        shadowColor: CONSULTA_PIEZAS_COLORS.SHADOW_BRAND,
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.2,
                        shadowRadius: 3,
                        elevation: 4,
                        borderWidth: 1,
                        borderColor: CONSULTA_PIEZAS_COLORS.MO_HAS_PROCESSES,
                      }}
                      onPress={() => handleMOClick(mo)}>
                      <Text
                        style={{
                          position: "absolute",
                          left: 10,
                          fontSize: 16,
                          fontWeight: "bold",
                          color: CONSULTA_PIEZAS_TABLA_COLORS.TEXT_MUTED,
                        }}>
                        {index + 1}.
                      </Text>

                      <View
                        style={{
                          flex: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}>
                        <Text
                          style={{
                            fontSize: 16,
                            fontWeight: "500",
                            color: CONSULTA_PIEZAS_COLORS.TEXT_PRIMARY,
                            textAlign: "center",
                          }}>
                          {mo.mo || translateText("Sin datos", lang)}
                        </Text>
                        <Text
                          style={{
                            fontSize: 12,
                            color: CONSULTA_PIEZAS_COLORS.TEXT_TERTIARY,
                            textAlign: "center",
                            marginTop: 2,
                          }}>
                          {mo.cliente} - {mo.referencia} -
                          {" " +
                            parseFloat(mo.cantidadPendiente || 0).toFixed(0)}
                        </Text>
                        <Text
                          style={{
                            fontSize: 12,
                            color: CONSULTA_PIEZAS_COLORS.SUCCESS_DARK,
                            textAlign: "center",
                            fontWeight: "bold",
                            marginTop: 2,
                          }}>
                          {calcularPrecioIndividual(mo).toFixed(2)} €
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ))
                ) : (
                  <Text
                    style={{
                      fontSize: 16,
                      color: CONSULTA_PIEZAS_COLORS.TEXT_PLACEHOLDER,
                      textAlign: "center",
                      marginTop: 20,
                    }}>
                    No se encontraron datos.
                  </Text>
                )}
              </View>
            </ScrollView>

            <TouchableOpacity
              style={{
                marginTop: 20,
                backgroundColor: CONSULTA_PIEZAS_COLORS.BACKGROUND_BUTTON,
                paddingVertical: 12,
                borderRadius: 8,
                alignItems: "center",
              }}
              onPress={handleCloseModal}>
                              <Text style={{ color: TEXT_COLORS.LIGHT, fontWeight: "bold", fontSize: 16 }}>
                Cerrar
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default ConsultaPiezasTabla;
